/**
 * @file Manual payments (T1.5) — the product law that guards the fields, the one
 * movement a recorded payment performs, and the arithmetic that decides where the
 * charge it settles ends up.
 *
 * Four things live here, and they are together because they are one act seen from
 * four angles: *a landlord received money, and here is what that means.*
 *
 *   1. **The PAN-pattern rejection** (`findPanLikeDigitRun`, `hasPanLikeDigitRun`).
 *      `_factory/conventions.md` §Product law: "No SSNs, no bank account numbers
 *      in our DB. Manual-payment fields reject PAN-like patterns (13–19 digit
 *      sequences). The Restricted data class stays EMPTY." This is the ONLY place
 *      that rule is expressed, it is a pure function, and it is applied to every
 *      free-text field a landlord can type on a manual payment — the memo and the
 *      reference. It is product law, not a nicety: it must not be weakened to make
 *      a test pass, and a rejection must never echo the offending value back.
 *   2. **The §6.1 move a payment makes** (`PAYMENT_RENT_CHARGE_MOVES`,
 *      `rentChargeStatusForPayment`). A payment is not a landlord's state-machine
 *      action; it is money arriving, and where the charge lands is ARITHMETIC: a
 *      full settlement is `paid`, a partial one is `partially_paid`. Which edges
 *      that implies, and why they are not simply `RENT_CHARGE_TRANSITIONS`, is the
 *      documented decision below.
 *   3. **The per-charge arithmetic** (`appliedCentsByCharge`, `chargeBalances`,
 *      `totalOutstandingCents`, `paymentsTotalCents`, `perTenantPaymentTotals`).
 *      An obligation is a `rent_charges` row and the money applied to it is a
 *      ledger row, so "what is still owed" is a subtraction the ROUTE must not
 *      re-derive. Everything is integer cents and every read of the ledger goes
 *      through `countedLedgerEntries`, so a corrected payment cannot be counted
 *      twice.
 *   4. **The write schema and the DTOs.** The body carries the amount as a POSITIVE
 *      magnitude — a landlord types how much they received — and the route is what
 *      negates it into the ledger's sign convention (positive = the tenant owes
 *      more).
 *
 * The kernel discipline applies as everywhere in `packages/shared`: no drizzle, no
 * app code. The SPA imports these TYPES and the validator's rule; the API remains
 * the authority on what may be written.
 */
import { z } from "zod";
import {
  countedLedgerEntries,
  perTenantBalances,
  type LedgerChannel,
  type LedgerEntryLike,
  type LedgerListRowDto,
  type LedgerPaymentState,
} from "./ledger.js";
import type { RentChargeStatus } from "./rent-charges.js";

import { optionalPanFree } from "./pan.js";
// ---------------------------------------------------------------------------
// Product law — no card numbers, ever. Defined in ./pan.ts (which imports nothing
// of ours) so ledger.ts can share the rule without a cycle; re-exported here so the
// kernel index and every existing import keep working.
// ---------------------------------------------------------------------------
export {
  PAN_DIGIT_RUN_MIN,
  PAN_DIGIT_RUN_MAX,
  PAN_REJECTION_MESSAGE,
  findPanLikeDigitRun,
  hasPanLikeDigitRun,
  requiredPanFree,
} from "./pan.js";
export type { PanLikeDigitRun } from "./pan.js";

// ---------------------------------------------------------------------------
// The movement a payment performs (§6.1)
// ---------------------------------------------------------------------------

/**
 * The channels a landlord may record by hand.
 *
 * `online` is deliberately absent: it is the ACH rail (T2.1+), and a manual
 * recording route that accepted it would let a landlord assert a movement Stripe
 * never made. Cards are NOT offered at launch (product law), which is why the
 * three manual instruments are the whole list.
 */
export const MANUAL_PAYMENT_CHANNELS = ["cash", "check", "other"] as const;
export type ManualPaymentChannel = (typeof MANUAL_PAYMENT_CHANNELS)[number];
export const MANUAL_PAYMENT_CHANNEL_VALUES = MANUAL_PAYMENT_CHANNELS.map(
  (channel) => channel,
) as [ManualPaymentChannel, ...ManualPaymentChannel[]];

/**
 * The states a landlord may assert for a manual payment.
 *
 * `settled` is the default because recording money received IS the confirmation:
 * cash is in hand and a deposited check has been seen by the person writing the
 * row. `pending` exists for the one case the recorder genuinely knows about — a
 * check that has not cleared — and §7.2's "dashboards distinguish the two" is
 * satisfied by reporting both. `n/a` and `returned` are NOT accepted: a payment is
 * a payment, and a return is the T2.x reversal flow, not something a landlord types.
 */
export const MANUAL_PAYMENT_STATES = ["pending", "settled"] as const;
export type ManualPaymentState = (typeof MANUAL_PAYMENT_STATES)[number];
export const MANUAL_PAYMENT_STATE_VALUES = MANUAL_PAYMENT_STATES.map(
  (state) => state,
) as [ManualPaymentState, ...ManualPaymentState[]];

/**
 * **The §6.1 edges a recorded PAYMENT may make**, as data.
 *
 * Deliberately NOT `RENT_CHARGE_TRANSITIONS` (packages/shared/src/rent-charges.ts).
 * That map governs the LANDLORD's transitions route, where T1.4 read §6.1 narrowly
 * on purpose: `due → paid` is not drawn there because §6.1's happy chain runs
 * *through* `partially_paid`, and because in a build that could not yet record a
 * payment, every edge had to be a deliberate landlord act. T1.4's card left the
 * decision here: *"when the payment task (T1.5) needs it, that is a plan change
 * with a stated reason, not a local convenience."*
 *
 * This is that reason. A payment is not a landlord's state-machine move — it is
 * money arriving, and where the charge lands is arithmetic:
 *
 *   * a charge fully settled is `paid`, whatever state it was in;
 *   * a charge part-settled is `partially_paid`;
 *   * `scheduled` is included because a charge is materialized §6.4's ten days
 *     before its due date, and a tenant paying early is an ordinary fact;
 *   * `partially_paid → partially_paid` and `late → late` are NOT moves: the
 *     arithmetic says nothing changed, so nothing is written and no audit row is
 *     fabricated. (`late → partially_paid` IS drawn — §6.1 does, and a part
 *     payment on a late charge is exactly what it describes.)
 *   * `paid` and `written_off` are terminal here. A charge collected after being
 *     written off is a ledger correction, not a state move (the same ruling T1.4's
 *     card records), and money arriving on a settled charge is an overpayment that
 *     shows as a credit rather than reopening the period.
 *
 * `Record<RentChargeStatus, …>` is total, so a state added to §6.1 without saying
 * what a payment does to it fails to compile.
 */
export const PAYMENT_RENT_CHARGE_MOVES: Readonly<
  Record<RentChargeStatus, readonly RentChargeStatus[]>
> = {
  scheduled: ["partially_paid", "paid"],
  due: ["partially_paid", "paid"],
  partially_paid: ["paid"],
  late: ["partially_paid", "paid"],
  paid: [],
  written_off: [],
};

/** May a recorded payment move a charge from `from` to `to`? One question. */
export function canPaymentMoveRentCharge(
  from: RentChargeStatus,
  to: RentChargeStatus,
): boolean {
  return PAYMENT_RENT_CHARGE_MOVES[from].includes(to);
}

/** The facts §6.1's arithmetic reads. Facts, not rows — so it needs no database. */
export interface RentChargePaymentFacts {
  /** Where the charge is now. */
  readonly status: RentChargeStatus;
  /** What the charge was for, in integer cents (`rent_charges.amount_cents`). */
  readonly chargeAmountCents: number;
  /** Payments already counted against THIS charge, as a positive magnitude. */
  readonly appliedCents: number;
  /** This payment, as a positive magnitude. */
  readonly paymentCents: number;
}

/**
 * **Where a recorded payment leaves the charge** — `paid`, `partially_paid`, or
 * nowhere.
 *
 * The arithmetic, in one sentence: what the charge was for, against everything that
 * has been applied to it including this payment. `total >= amount` is `paid`
 * (an OVERPAYMENT is still `paid` — the excess is a credit on the ledger, not a
 * reason to refuse the money), anything less is `partially_paid`.
 *
 * `null` means **no move is warranted**, and it is a real answer rather than a
 * failure: the charge is already in the state the arithmetic dictates (a second
 * part payment), or it is terminal (`paid` with an overpayment, `written_off` with
 * money arriving after the debt was released). The caller writes no status row and
 * the audit trail stays free of moves that did not happen.
 *
 * A target the payment map does not draw is a programming error, not a 409: the map
 * is total over the six states, so this can only fire if a state was added to §6.1
 * and not to `PAYMENT_RENT_CHARGE_MOVES`.
 */
export function rentChargeStatusForPayment(
  facts: RentChargePaymentFacts,
): RentChargeStatus | null {
  for (const [label, value] of [
    ["chargeAmountCents", facts.chargeAmountCents],
    ["appliedCents", facts.appliedCents],
    ["paymentCents", facts.paymentCents],
  ] as const) {
    if (!Number.isInteger(value)) {
      throw new Error(
        `payments: ${label} must be a whole number of cents — money is INTEGER CENTS (CLAUDE.md)`,
      );
    }
  }
  if (facts.paymentCents <= 0) {
    throw new Error("payments: a payment must be a positive number of cents");
  }

  if (facts.status === "paid" || facts.status === "written_off") return null;

  const total = facts.appliedCents + facts.paymentCents;
  const target: RentChargeStatus = total >= facts.chargeAmountCents ? "paid" : "partially_paid";

  // Nothing to record: the charge already says exactly this.
  if (target === facts.status) return null;

  if (!canPaymentMoveRentCharge(facts.status, target)) {
    throw new Error(
      `payments: a recorded payment would move a rent charge ${facts.status} → ${target}, ` +
        "which PAYMENT_RENT_CHARGE_MOVES does not draw — add the state to §6.1 and to the map",
    );
  }
  return target;
}

// ---------------------------------------------------------------------------
// The per-charge arithmetic
// ---------------------------------------------------------------------------

/** The fields a ledger row must carry to be applied to a charge. */
export interface PaymentEntryLike extends LedgerEntryLike {
  /** The charge this entry was applied to; NULL means "not attributed". */
  readonly rentChargeId: string | null;
  /** The payment lifecycle state — `settled` and `pending` are read apart (§7.2). */
  readonly paymentState: LedgerPaymentState;
}

/**
 * How much money has been applied to each charge, as a positive magnitude.
 *
 * Counted rows only (`countedLedgerEntries` — the ONE balance rule, which handles
 * corrections and supersession), `entry_type = 'payment'` only, and only rows that
 * name a charge. A corrected payment is counted at its corrected value exactly
 * once, and an unlinked payment contributes to the lease's money but to no charge's
 * — which is the honest reading of a row nobody attributed.
 */
export function appliedCentsByCharge(
  entries: readonly PaymentEntryLike[],
): ReadonlyMap<string, number> {
  const totals = new Map<string, number>();
  for (const entry of countedLedgerEntries(entries)) {
    if (entry.entryType !== "payment") continue;
    if (entry.rentChargeId === null) continue;
    // Payments are NEGATIVE on the ledger (they reduce what is owed); the
    // arithmetic below is about magnitudes applied, so the sign is dropped here
    // and nowhere else.
    const magnitude = Math.abs(entry.amountCents);
    totals.set(entry.rentChargeId, (totals.get(entry.rentChargeId) ?? 0) + magnitude);
  }
  return totals;
}

/** The minimum a charge must expose to be weighed against the money applied. */
export interface ChargeLike {
  readonly id: string;
  readonly amountCents: number;
  readonly status: RentChargeStatus;
}

/** One charge, and where it has got to. */
export interface ChargeBalance {
  readonly chargeId: string;
  readonly amountCents: number;
  readonly appliedCents: number;
  /** Integer cents, never negative: an overpayment is a credit, not a debt. */
  readonly outstandingCents: number;
  readonly status: RentChargeStatus;
  /** Is money still owed on this charge at all? `written_off` is not. */
  readonly unpaid: boolean;
}

/**
 * Every charge with the money applied against it.
 *
 * `outstandingCents` is clamped at zero per charge, deliberately: an overpayment on
 * one period is a CREDIT, and letting it subtract from another period's debt would
 * be applying money the landlord attributed elsewhere. That is the same rule as the
 * per-charge link itself.
 */
export function chargeBalances(
  charges: readonly ChargeLike[],
  entries: readonly PaymentEntryLike[],
): readonly ChargeBalance[] {
  const applied = appliedCentsByCharge(entries);
  return charges.map((charge) => {
    const appliedCents = applied.get(charge.id) ?? 0;
    return {
      chargeId: charge.id,
      amountCents: charge.amountCents,
      appliedCents,
      outstandingCents: Math.max(0, charge.amountCents - appliedCents),
      status: charge.status,
      // `written_off` released the debt, so it owes nothing for this purpose even
      // though its arithmetic says otherwise. Every other state — including a `paid`
      // charge whose payment was later corrected away — reports what the money says.
      unpaid: charge.status !== "written_off",
    };
  });
}

/**
 * What is still owed across a set of charges.
 *
 * Sums the per-charge remainders, so an overpayment on one period never cancels
 * another period's debt, and a written-off charge contributes nothing.
 */
export function totalOutstandingCents(balances: readonly ChargeBalance[]): number {
  let total = 0;
  for (const balance of balances) {
    if (balance.unpaid) total += balance.outstandingCents;
  }
  return total;
}

/**
 * The money recorded through a channel, or across the whole ledger.
 *
 * Counted payment rows only, as a positive magnitude, optionally filtered by
 * `payment_state` so a screen can say "$4,000 settled, $1,850 pending" without a
 * second rule. `state: null` means every state.
 */
export function paymentsTotalCents(
  entries: readonly PaymentEntryLike[],
  state: LedgerPaymentState | null = null,
): number {
  let total = 0;
  for (const entry of countedLedgerEntries(entries)) {
    if (entry.entryType !== "payment") continue;
    if (state !== null && entry.paymentState !== state) continue;
    total += Math.abs(entry.amountCents);
  }
  return total;
}

/** One bucket of the per-tenant money view. `null` is the unattributed bucket. */
export interface LedgerAttributionTotal {
  readonly leaseTenantId: string | null;
  /** Integer cents of money RECEIVED against this person's entries. */
  readonly totalCents: number;
}

/**
 * The per-tenant view of money RECEIVED, built from the rows themselves.
 *
 * Delegates the bucketing to `perTenantBalances` — the ONE split rule — over the
 * payment rows alone, and negates, because a payment is negative on the ledger
 * (it reduces what is owed) while this view reports money in. No invented zero for
 * a person with nothing recorded: a bucket exists only when a payment created it,
 * and `null` collects the unattributed money, which on a joint lease is the
 * ordinary case.
 *
 * **This is what the per-tenant view honestly supports.** It says what each person
 * PAID; it does not say what each person OWES, because on a joint lease the
 * household owes the rent and an allocation would be an invention — the API says so
 * in the response shape (`owesCents: null` with a reason) rather than dividing a
 * number nobody divided.
 */
export function perTenantPaymentTotals(
  entries: readonly PaymentEntryLike[],
): readonly LedgerAttributionTotal[] {
  return perTenantBalances(entries.filter((entry) => entry.entryType === "payment")).map(
    (bucket) => ({ leaseTenantId: bucket.leaseTenantId, totalCents: -bucket.balanceCents }),
  );
}

/** Why a per-person OWES figure is absent, in one sentence, for the response. */
export const JOINT_LEASE_NO_ALLOCATION_REASON =
  "the household owes this lease; a joint lease has no per-person split, and the " +
  "platform does not invent one";

/**
 * The same refusal for a SPLIT lease, which is the case worth stating precisely.
 *
 * A split lease DOES record each person's share (`lease_tenants.share_type`), so
 * "there is no split" would be false. What the platform refuses is turning those
 * shares into an obligation: `percent` shares need not total 100, a `fixed` share is
 * in cents, and nothing reconciles them against the charge — so dividing the
 * outstanding would be arithmetic the landlord never performed, presented as a bill.
 * The per-tenant view reports what each person PAID and says why it stops there.
 */
export const SPLIT_LEASE_NO_ALLOCATION_REASON =
  "this lease records each person's share of the rent, but the platform does not " +
  "turn a share into a per-person obligation; the lease owes the rent, and dividing " +
  "it would be an allocation nobody made";

// ---------------------------------------------------------------------------
// Input schema
// ---------------------------------------------------------------------------

/**
 * POST /workspaces/:workspaceId/payments body.
 *
 * Not accepted here, each for a reason:
 *
 *   * `entryType` — a manual payment is a `payment`, always. A body that could
 *     choose it would be the generic ledger append with a different URL.
 *   * the sign — `amountCents` is the MAGNITUDE the landlord received, because
 *     that is what a person types ("$1,850"). The route negates it into the
 *     ledger's convention, so a client cannot flip a payment into a charge.
 *   * `leaseId` — inherited from the named charge. A payment settles a period of a
 *     lease; letting the body name a different lease is how money lands on the
 *     wrong agreement.
 *   * `paymentState` values other than `pending | settled`, and the Stripe
 *     references: nothing in this build may assert a payment Stripe never made.
 *   * `superseded`/`correctionOf` — a new row states a fact; replacing one is the
 *     correction route's job, and the ledger's append-only trigger would refuse
 *     anything else anyway.
 */
export const paymentCreateSchema = z.object({
  /** The charge this payment settles. Required: money is applied to a period. */
  rentChargeId: z.string().uuid("must be a rent charge id"),
  /**
   * Integer cents, POSITIVE — how much the landlord received. Never a float, never
   * zero: a payment that records no money is not a payment.
   */
  amountCents: z
    .number()
    .int("money must be whole cents — no decimals")
    .positive("a payment records money received, so the amount must be more than zero"),
  /** How the money arrived: cash, check, or another manual instrument. */
  channel: z.enum(MANUAL_PAYMENT_CHANNEL_VALUES),
  /** Optional attribution to one person on the lease. NULL is the ordinary case. */
  leaseTenantId: z
    .string()
    .uuid("must be a lease tenant id")
    .nullish()
    .transform((value) => value ?? null),
  /** Free text, PAN-free (product law). What the payment was for. */
  memo: optionalPanFree(500),
  /** A check number, money-order number or transfer reference. PAN-free. */
  reference: optionalPanFree(120),
  /** `settled` unless the landlord says otherwise (an uncleared check). */
  paymentState: z.enum(MANUAL_PAYMENT_STATE_VALUES).default("settled"),
  /**
   * The client's id for THIS submission — minted once when the form is submitted
   * and sent again on every retry of it. Stored as `ledger_entries.idempotency_key`
   * (migration 0020), unique per workspace: a retried POST returns the payment it
   * already recorded (200, `replayed: true`) rather than recording money twice, and
   * the same key with a different payment is refused (409). Optional, so a client
   * that does not send one still records — it simply forgoes the replay.
   */
  clientRequestId: z
    .string()
    .uuid("must be a UUID")
    .nullish()
    .transform((value) => value ?? null),
});

export type PaymentCreateInput = z.infer<typeof paymentCreateSchema>;

/** The PRE-parse shape, for the SPA to build a request body from. */
export type PaymentCreateBody = z.input<typeof paymentCreateSchema>;

// ---------------------------------------------------------------------------
// DTOs — what the API returns
// ---------------------------------------------------------------------------

/**
 * One recorded payment, as the API returns it.
 *
 * Every money field is integer cents and the SIGN is the ledger's (`amountCents` is
 * negative — a payment reduces what the tenant owes); `amountReceivedCents` is the
 * same number as the landlord experienced it, so no screen has to remember to flip
 * a sign to render money in.
 */
export interface PaymentDto {
  readonly id: string;
  readonly leaseId: string;
  /** The charge this payment was applied to, or NULL when it named none. */
  readonly rentChargeId: string | null;
  readonly leaseTenantId: string | null;
  /** Signed integer cents, NEGATIVE: money received reduces what is owed. */
  readonly amountCents: number;
  /** The same amount as a positive magnitude — what the landlord received. */
  readonly amountReceivedCents: number;
  readonly paymentState: LedgerPaymentState;
  readonly channel: LedgerChannel | null;
  readonly memo: string | null;
  /** The instrument reference, or NULL. Never a card or account number. */
  readonly reference: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

/**
 * A payment plus the context that makes it identifiable: the lease, the unit, the
 * property and (when attributed) the person.
 *
 * Extends `LedgerListRowDto` so the two money lists cannot disagree about what a
 * ledger row's context looks like, and adds exactly the two fields a manual
 * payment carries that a generic ledger row does not.
 */
export interface PaymentListRowDto extends LedgerListRowDto {
  /** The charge this payment was applied to, or NULL. */
  readonly rentChargeId: string | null;
  /** The instrument reference, or NULL. */
  readonly reference: string | null;
  /** The same signed amount as a positive magnitude. */
  readonly amountReceivedCents: number;
}

/**
 * GET /workspaces/:workspaceId/payments — the payment history.
 *
 * `counts` and `byChannel` are folded from the rows beside them, so every figure
 * can be checked against the list. `settledCents` and `pendingCents` are separate
 * because §7.2 requires the dashboard to distinguish them — and a single "collected"
 * number would silently decide that an uncleared check is money in the bank.
 */
export interface PaymentListDto {
  readonly counts: {
    /** Payments a money figure may be summed over (the one balance rule). */
    readonly payments: number;
    /**
     * Rows returned, including a payment that has been corrected and the correction
     * that replaced it. Separate from `payments` for the same reason the ledger's
     * counts are: "2 payments received" and "3 rows written" are different claims.
     */
    readonly listed: number;
    /** Distinct leases with at least one payment — not the count of payments. */
    readonly leases: number;
    readonly settledCents: number;
    readonly pendingCents: number;
  };
  /** Only the channels that actually occur, in the manual-instrument order. */
  readonly byChannel: readonly {
    readonly channel: LedgerChannel;
    readonly payments: number;
    readonly receivedCents: number;
  }[];
  /** One page, newest first (?limit=, ?cursor=). `counts` and `byChannel` cover every payment. */
  readonly payments: readonly PaymentListRowDto[];
  /** Pass as `?cursor=` for the next page; null on the last page. */
  readonly nextCursor: string | null;
}

/**
 * What recording one payment did.
 *
 * Returned by the write route so the caller sees the whole movement rather than
 * having to re-read the charge: the row appended, where the charge landed (and
 * where it was), and — when the payment was bigger than the debt —
 * `overpaymentCents`, which is a CREDIT on the ledger and never discarded.
 *
 * On a REPLAY (`replayed: true`, HTTP 200) the same shape carries the row the same
 * `clientRequestId` recorded earlier and the charge's CURRENT figures: `status`
 * equals `previousStatus`, `moved` is false, and `appliedCents` is everything
 * applied to the charge now, that row included.
 */
export interface PaymentRecordedDto {
  readonly payment: PaymentDto;
  readonly charge: {
    readonly id: string;
    readonly status: RentChargeStatus;
    readonly previousStatus: RentChargeStatus;
    readonly amountCents: number;
    readonly appliedCents: number;
    readonly outstandingCents: number;
    /** Did this payment actually move the charge? A second part payment does not. */
    readonly moved: boolean;
  };
  /** Integer cents of this payment that exceeded what the charge still owed. */
  readonly overpaymentCents: number;
  /**
   * True when nothing was recorded because the same `clientRequestId` already
   * had: the response describes the earlier payment. False on every fresh record.
   */
  readonly replayed: boolean;
}

/** One charge in the unit-owes view, with the money applied against it. */
export interface ChargeBalanceDto extends ChargeBalance {
  readonly periodStart: string;
  readonly periodEnd: string;
  readonly dueDate: string;
}

/**
 * One person on the lease, in the per-tenant view.
 *
 * `owesCents` is `null` BY DESIGN and carries the reason: the platform reports what
 * a person PAID (attribution is a fact a landlord records on the payment) and
 * refuses to divide an obligation nobody divided. A joint lease has no per-person
 * split at all, and a split lease's shares are a landlord's own arithmetic — see
 * `JOINT_LEASE_NO_ALLOCATION_REASON` and `packages/shared/src/leases.ts`.
 */
export interface PerTenantBalanceDto {
  readonly leaseTenantId: string;
  readonly fullName: string;
  /** Integer cents of payments attributed to this person. */
  readonly paidCents: number;
  /** Always NULL in this build; read `owesReason` for why. */
  readonly owesCents: null;
  readonly owesReason: string;
}

/**
 * One lease's obligations and the money applied to them — the "unit owes" view.
 *
 * Two figures that are easy to confuse and are therefore both present:
 * `outstandingCents` is EVERYTHING unpaid (including periods not yet due), and
 * `dueCents` is only what has fallen due by `asOfDate` in THIS property's zone. The
 * second is the one a landlord acts on, and it is computed server-side against the
 * building's calendar rather than the browser's (the T1.4 rule: the property owns
 * the calendar).
 */
export interface LeaseBalanceDto {
  readonly leaseId: string;
  readonly leaseStatus: string;
  readonly leaseMode: string;
  readonly unitId: string;
  readonly unitLabel: string;
  readonly propertyId: string;
  readonly propertyLabel: string | null;
  readonly addressLine1: string;
  readonly city: string;
  readonly state: string;
  /** Today, in the property's zone — the calendar `dueCents` was decided on. */
  readonly asOfDate: string;
  readonly chargedCents: number;
  readonly paidCents: number;
  readonly outstandingCents: number;
  readonly dueCents: number;
  readonly charges: readonly ChargeBalanceDto[];
  readonly perTenant: readonly PerTenantBalanceDto[];
}

/**
 * GET /workspaces/:workspaceId/balances — the portfolio's unit-owes view.
 *
 * `counts` repeats the portfolio totals so a header does not have to add the list
 * up, and every one of them is the sum of the rows below it.
 */
export interface PortfolioBalancesDto {
  /** True when the list was cut at the server cap: these are the first rows, not all of them. */
  readonly truncated?: true;
  readonly counts: {
    readonly leases: number;
    readonly charges: number;
    /** Charges with money still owed — not the count of charges. */
    readonly chargesOutstanding: number;
    readonly chargedCents: number;
    readonly paidCents: number;
    readonly outstandingCents: number;
    readonly dueCents: number;
  };
  readonly balances: readonly LeaseBalanceDto[];
}

// ---------------------------------------------------------------------------
// The portfolio money summary (the dashboard's Money section)
// ---------------------------------------------------------------------------

/**
 * The four figures the dashboard's Money section renders, each derived from rows
 * that exist.
 *
 * Every one carries the COUNT it was folded from, so a screen can say "no charges
 * yet" instead of rendering `$0.00 collected` — a difference the whole product
 * insists on: `$0` reads as a fact about the world ("nobody paid") when it is only
 * a fact about the build.
 */
export interface PortfolioMoneyDto {
  /** Distinct units with a lease in `active` or `month_to_month`. */
  readonly leasedUnits: number;
  readonly totalUnits: number;
  readonly charges: number;
  readonly payments: number;
  /** Everything charged that is still unpaid, due or not. */
  readonly outstandingCents: number;
  /** Only the charges whose due date has passed, in their property's zone. */
  readonly dueCents: number;
  readonly collectedCents: number;
  readonly pendingCents: number;
}
